import { Reveal } from "@/components/Reveal";
import { Section, Eyebrow, PageHero } from "@/components/common";
import { useContent } from "@/lib/useContent";

const GALLERY = [
  { src: "/moment1.jpg", alt: "Satish Chapagain in a campaign shirt at a community barn gathering in District 7A" },
  { src: "/moment2.webp", alt: "Satish Chapagain talking with students and residents on a college campus" },
  { src: "/moment3.webp", alt: "Satish Chapagain at home with his dog" },
];

export default function About() {
  const { data: content } = useContent();
  if (!content) return <div className="h-screen" />;
  const about = content.about || {};

  return (
    <div>
      <PageHero eyebrow="Meet Satish" title="A life of service to District 7A" subtitle="Husband, father, community builder, and problem solver — ready to bring our neighbors' voices to Annapolis." />

      <Section className="py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <img src="/satish-portrait-45.jpg" alt="Satish Chapagain, candidate for Maryland State Delegate, District 7A" className="rounded-3xl shadow-xl w-full object-cover object-center aspect-[4/5] sticky top-24" data-testid="about-portrait" />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <Eyebrow>Biography</Eyebrow>
            <h2 className="font-serif text-3xl font-bold text-navy">About Satish</h2>
            <p className="mt-4 text-lg text-slate-700 leading-relaxed">{about.bio}</p>

            <div className="mt-10 bg-sky/60 border border-sky-border rounded-2xl p-7">
              <Eyebrow>In His Own Words</Eyebrow>
              <h3 className="font-serif text-2xl font-bold text-navy">Why I'm Running</h3>
              <p className="mt-3 text-slate-700 leading-relaxed font-serif italic text-lg">"{about.why_running}"</p>
              <p className="mt-4 font-serif font-bold text-navy">— Satish Chapagain</p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* TIMELINE */}
      <Section className="py-10">
        <Reveal className="text-center max-w-3xl mx-auto mb-12">
          <Eyebrow>A Record of Service</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy">
            From Our Neighborhoods,<br />For Our Neighborhoods
          </h2>
        </Reveal>
        <div className="max-w-3xl mx-auto relative">
          <div className="absolute left-5 sm:left-6 top-2 bottom-2 w-0.5 bg-gradient-to-b from-brightblue via-sky-border to-brightblue/40" />
          {(about.timeline || []).map((t, i) => (
            <Reveal key={t.year} delay={i * 0.05} className="relative pl-16 sm:pl-20 mb-10 last:mb-0" data-testid={`timeline-${i}`}>
              <div className="absolute left-0 top-0 w-12 h-12 sm:w-13 sm:h-13 rounded-full hero-gradient text-white flex items-center justify-center font-serif font-bold text-sm shadow-lg ring-4 ring-white" data-testid={`timeline-year-${i}`}>
                {t.year}
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-shadow p-6">
                {t.title && <h3 className="font-serif text-lg sm:text-xl font-bold text-navy">{t.title}</h3>}
                <p className="mt-2 text-slate-600 text-sm sm:text-base leading-relaxed">{t.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        {about.timeline_closing && (
          <Reveal className="max-w-3xl mx-auto mt-10">
            <div className="bg-sky/60 border-l-4 border-brightblue rounded-r-2xl p-6">
              <p className="font-serif italic text-lg text-navy leading-relaxed">{about.timeline_closing}</p>
            </div>
          </Reveal>
        )}
      </Section>

      {/* VALUES */}
      <Section className="py-14">
        <Reveal className="text-center max-w-2xl mx-auto mb-10">
          <Eyebrow>What Guides Him</Eyebrow>
          <h2 className="font-serif text-3xl font-bold text-navy">Family &amp; community values</h2>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <Reveal className="order-2 lg:order-1">
            <img src="/satish-family.webp" alt="Satish Chapagain with his wife and daughters" className="rounded-3xl shadow-xl w-full object-cover aspect-[4/5]" data-testid="about-family" />
            <p className="text-sm text-slate-500 mt-3 italic text-center">Satish with his wife and daughters — family is the heart of everything he does.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 order-1 lg:order-2">
            {(about.values || []).map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all p-6" data-testid={`value-${i}`}>
                <h3 className="font-serif text-lg font-bold text-navy">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* GALLERY */}
      <Section className="pb-20">
        <Reveal className="text-center max-w-2xl mx-auto mb-8">
          <Eyebrow>On the Campaign Trail</Eyebrow>
          <h2 className="font-serif text-3xl font-bold text-navy">Community moments</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {GALLERY.map((img, i) => (
            <Reveal key={img.src} delay={i * 0.08}>
              <img src={img.src} alt={img.alt} className="rounded-2xl shadow-md w-full object-cover aspect-[4/3] hover:scale-[1.02] transition-transform" data-testid={`gallery-${i}`} />
            </Reveal>
          ))}
        </div>
      </Section>
    </div>
  );
}
